"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { FileDown, Gift } from "lucide-react";
import { CallbackForm } from "@/components/callback-form";
import { Container } from "@/components/shared/container";
import { BROCHURE } from "@/lib/constants";
import { cn } from "@/lib/utils";

/* ────────────────────────────────────────────────────────────
   Wspólne klocki
   ──────────────────────────────────────────────────────────── */
export const SOFT = "#F5F7FB"; // chłodna biel na sekcje naprzemienne

export function Eyebrow({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <p
      className={`mb-3 text-sm font-medium uppercase tracking-wide ${
        dark ? "text-[#8FB8FF]" : "text-primary"
      }`}
    >
      {children}
    </p>
  );
}

export function SectionHead({
  eyebrow,
  title,
  lead,
  dark,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  dark?: boolean;
}) {
  return (
    <Reveal className="mx-auto max-w-3xl text-center">
      <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
      <h2 className="text-balance text-3xl font-bold tracking-tight md:text-5xl">{title}</h2>
      {lead && (
        <p className={`mt-4 text-base md:text-lg ${dark ? "text-white/85" : "text-muted-foreground"}`}>
          {lead}
        </p>
      )}
    </Reveal>
  );
}

/** Wejście przy scrollu — szanuje prefers-reduced-motion. */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Screen z systemu w oknie aplikacji, „wychodzący" poza kadr na niebieskim tle. */
export function ScreenOnSky({ src, alt, w, h }: { src: string; alt: string; w: number; h: number }) {
  return (
    <div className="absolute inset-0">
      <div
        aria-hidden
        className="absolute -right-16 -top-20 h-72 w-72 rounded-full bg-white/40 blur-3xl"
      />
      <div className="absolute left-[8%] right-[8%] top-[11%] overflow-hidden rounded-xl bg-white shadow-[0_30px_70px_-20px_rgba(2,26,158,0.55)] ring-1 ring-white/70 md:rounded-2xl">
        <div className="flex items-center gap-1 border-b border-black/5 bg-[#FAFAFA] px-3 py-1.5">
          <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
          <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
          <span className="h-2 w-2 rounded-full bg-[#28C840]" />
        </div>
        <Image src={src} alt={alt} width={w} height={h} sizes="(min-width: 1024px) 700px, 100vw" className="h-auto w-full" />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Niebo w barwach Vetiny — same gradienty, 0 KB obrazów
   ──────────────────────────────────────────────────────────── */
export function Sky({ variant = "day" }: { variant?: "day" | "dusk" | "deep" | "page" }) {
  if (variant === "deep") {
    // Jednolity, głęboki niebieski — pod białe liczby i listy (kontrast AA)
    return (
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ background: "linear-gradient(160deg, #021A9E 0%, #0528F2 55%, #1F4FE8 100%)" }}
      />
    );
  }
  // Dzień: górne ~60% zostaje nasycone (tekst), jaśnieje dopiero przy screenie produktu
  const base =
    variant === "day"
      ? "linear-gradient(180deg, #0528F2 0%, #1C45EE 30%, #2F64F0 50%, #6FA8F5 72%, #BFDCFA 88%, #EEF5FF 100%)"
      : variant === "page"
        ? // Podstrony: góra jak pasek promo, cały czas nasycone pod biały tekst
          "linear-gradient(180deg, #0528F2 0%, #1C45EE 45%, #3A6FF0 100%)"
        : "linear-gradient(180deg, #021A9E 0%, #0528F2 50%, #2C66F0 100%)";
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0" style={{ background: base }} />
      <div
        className="absolute top-0 right-[-14%] h-[560px] w-[560px] rounded-full opacity-40 blur-2xl"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(214,232,255,0.3) 32%, transparent 66%)",
        }}
      />
      {/* Chmury tylko w dolnej części — z dala od tekstu */}
      {[
        "left-[-10%] top-[58%] h-40 w-[45%]",
        "right-[-8%] top-[54%] h-32 w-[38%]",
        "left-[18%] top-[70%] h-52 w-[64%]",
        "left-[-12%] top-[80%] h-60 w-[56%]",
        "right-[-14%] top-[78%] h-64 w-[60%]",
      ].map((c) => (
        <div key={c} className={`absolute rounded-full blur-3xl ${variant === "day" ? "bg-white/50" : "bg-white/[0.1]"} ${c}`} />
      ))}
    </div>
  );
}

/** Końcowe CTA: formularz „oddzwonimy" na niebie. */
export function FinalCta({ source }: { source: string }) {
  return (
    <section id="demo" className="scroll-mt-24 px-2 pb-2 md:px-4 md:pb-4">
      <div className="relative overflow-hidden rounded-[28px] py-16 md:rounded-[40px] md:py-24">
        <Sky variant="dusk" />
        <Container className="relative max-w-2xl text-center text-white">
          <h2 className="text-balance text-3xl font-bold tracking-tight md:text-5xl">Zostaw numer. Oddzwonimy.</h2>
          <p className="mt-5 text-balance text-lg text-white/85 md:text-xl">
            15 sekund Twojego czasu. Resztę załatwimy w rozmowie — w tej kolejności, w której Tobie pasuje.
          </p>
          <div className="mt-8 inline-flex items-center gap-2.5 rounded-2xl bg-white py-2 pl-2 pr-4 text-left text-foreground shadow-lg shadow-[#021A9E]/25 sm:rounded-full">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white">
              <Gift className="h-4 w-4" />
            </span>
            <span className="text-sm">
              <strong className="font-semibold text-primary">0 zł za migrację</strong>, bez utraty danych z dowolnego systemu
            </span>
          </div>
          <div className="mt-10 rounded-[24px] bg-white p-6 text-left text-foreground shadow-2xl shadow-[#021A9E]/30 md:p-8">
            <CallbackForm source={source} />
          </div>
          <div className="mt-8 flex flex-col items-center gap-2 text-sm text-white/80">
            <span>Musisz przekonać właściciela placówki?</span>
            <a href={BROCHURE.href} download className="inline-flex items-center gap-1.5 font-medium text-white hover:underline">
              <FileDown className="h-4 w-4" />
              Pobierz raport z argumentami
            </a>
          </div>
        </Container>
      </div>
    </section>
  );
}

/** Zapętlony filmik bez dźwięku; gra tylko, gdy jest na ekranie. */
export function LoopVideo({ base, label, className, controls }: { base: string; label: string; className?: string; controls?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || controls) return;
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause()),
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [controls]);
  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      autoPlay={controls}
      controls={controls}
      preload="metadata"
      poster={`${base}-poster.webp`}
      aria-label={label}
      className={cn("block h-auto w-full", className)}
    >
      <source src={`${base}.webm`} type="video/webm" />
      <source src={`${base}.mp4`} type="video/mp4" />
    </video>
  );
}
